/* eslint-disable */
import React, { useState } from 'react';
import { format, isAfter, isBefore, isValid, parse } from 'date-fns';
import classnames from 'classnames';
import { TextInputFloating } from '@zoom/components';

import { DatePickerValueType } from './DatePicker';
import { useAuth } from '../../app/AuthorizationProvider';

export interface InputWrapperProps {
  className?: string;
  customClassName?: string;
  dataTestId?: string;
  dateFormat: string;
  datePickerRef: React.RefObject<any>;
  disabled?: boolean;
  inputId?: string;
  inputRef: string;
  label?: string;
  maxDate?: Date;
  minDate?: Date;
  name?: string;
  onBlur?: (event?: any) => null;
  onFocus?: (event?: any) => null;
  onKeyDown?: (event?: any) => null;
  onClick?: any;
  onChange?: any;
  customOnChange: (date: DatePickerValueType) => void;
  ref?: string;
  value?: Date | string;
}

export const DatePickerTextFieldInput: React.FC<InputWrapperProps> = ({
  className,
  customClassName,
  dataTestId,
  dateFormat,
  datePickerRef,
  disabled,
  inputId,
  inputRef,
  label,
  maxDate,
  minDate,
  onBlur,
  onClick,
  onKeyDown,
  customOnChange,
  value,
}) => {
  const { localeObj } = useAuth();

  const formattedValue = value ? format(new Date(value), dateFormat, { locale: localeObj }) : '';
  const [tempValue, setTempValue] = useState(formattedValue);

  const validate = (updatedValue?: string, blur = true) => {
    const updatedDate = updatedValue && parse(updatedValue, dateFormat, new Date());
    const roundedDate = dateInMatchWithBoundaries(updatedDate);
    if (roundedDate) {
      const formattedRoundedDate = format(roundedDate || '', dateFormat, { locale: localeObj });
      if (formattedRoundedDate !== formattedValue) {
        setTempValue(format(new Date(formattedRoundedDate), dateFormat, { locale: localeObj }));
        customOnChange(roundedDate);
      }
    } else {
      customOnChange(null);
      setTempValue('');
    }
    if (blur) {
      customOnBlur();
    }
  };

  const dateInMatchWithBoundaries = (date?: Date | '') => {
    if (!date || !isValid(date)) {
      return null;
    }

    if (minDate && isBefore(date, minDate)) {
      return minDate;
    }

    if (maxDate && isAfter(date, maxDate)) {
      return maxDate;
    }

    return date;
  };

  const customOnBlur = () => {
    if (datePickerRef) {
      datePickerRef.current.handleCalendarClickOutside();
      datePickerRef.current.cancelFocusInput();
    }
  };

  const onChangeHandler = (updatedValue: string) => {
    setTempValue(updatedValue);
  };

  const onKeyDownHandler = (event: any) => {
    if (event.key === 'Escape') {
      onChangeHandler(formattedValue);
      if (onKeyDown) {
        onKeyDown(event);
      }
      return;
    }
    if (event.key === 'Tab') {
      if (tempValue !== formattedValue) {
        validate(tempValue, false);
      }
      if (onKeyDown) {
        onKeyDown(event);
      }
      return;
    }
    if ((!tempValue && !formattedValue) || tempValue === formattedValue) {
      if (onKeyDown) {
        onKeyDown(event);
      }
    }
  };

  const onBlurHandler = (event: any) => {
    setTempValue(formattedValue);

    if (onBlur) {
      onBlur(event);
    }
  };

  return (
    <div className={classnames('date-picker-input', customClassName)}>
      <TextInputFloating
        aria-expanded={datePickerRef.current?.state?.open ?? false}
        autoComplete="off"
        className={className}
        data-test-id={dataTestId}
        disabled={disabled}
        id={inputId}
        inputRef={inputRef}
        label={label}
        name={inputId}
        onBlur={onBlurHandler}
        onFocus={onClick}
        onClick={onClick}
        onChange={onChangeHandler}
        onKeyDown={onKeyDownHandler}
        validate={validate}
        value={tempValue}
      />
    </div>
  );
};

export default DatePickerTextFieldInput;
